import { Fragment, useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { v4 as uuid } from 'uuid'

import { BlockContent } from '~/components/BlockContent'
import { Checkbox } from '~/components/Checkbox'
import { BootcampPageUrlParamsType, LevelActionType } from '~/bootcamp/types/bootcamp.types'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import { useSetBootcampDataMutation } from '~/bootcamp/apis/set-data.api'
import {
  selectBootcampPageContent,
  selectBootcampPageCompleted,
  selectBootcampPageEncyclopediaArticle,
} from '~/bootcamp/slices/bootcamp-page.slice'
import { selectVideoTopicColor } from '~/bootcamp/slices/vcc-data.slice'
import bootcampAnalytics from '~/analytics/bootcamp/bootcampAnalytics'
import { BCAEvent } from '~/analytics/bootcamp/bootcampEvents.types'
import styles from '~/shared/styles/SanityBlock.module.css'
import { CATEGORY_PATHS, CategoryColorClasses } from '~/bootcamp/constants/bootcamp.const'
import { useAddOtherActivityAttemptMutation } from '~/redux/apis/activity-api'
import { ActivityCategory, GenerationMethod } from '~/redux/apis/types'
import { ContentType } from '~/types/bootcamp'
import { isAuthenticated } from '~/auth/slices/auth.slice'
import useShowFunFactChest from '~/bootcamp/hooks/useShowFunFactChest'
import { Breakpoint, useCheckBreakpoint } from '~/utils'
import { htmlToPlainText } from '~/utils/htmlToPlainText'
import SVGIcon from '~/components/Icons/svgs/SVGIcon'
import MusicRobotIcon from '~/components/Icons/svgs/MusicRobotIcon'
import FeedbackButton from '~/user-feedback/components/FeedbackButton'
import { FeedbackProducts } from '~/user-feedback/constants/userFeedback.const'
import TipsTooltip from '~/components/Tooltip/TipsTooltip'
import { useUserSettings } from '~/shared/hooks/useUserSettings'
import useIsBootcampGradePage from '~/bootcamp/hooks/useIsBootcampGradePage'
import TheoryArticleView from '~/encyclopedia/views/TheoryArticleView'
import { setChecklistNotification } from '~/redux/slices/checklist'
import { REWARD_FEATURE } from '~/gamification/types/gamification.types'
import { setShowFreemiumModalCreator } from '~/redux/slices/modals'

import { NextLevel } from './NavigationButton'
import { FunFactChest } from './FunFactChest'

function VideoContentWrapper(): React.ReactElement {
  const content = useAppSelector(selectBootcampPageContent)
  const encyclopediaArticle = useAppSelector(selectBootcampPageEncyclopediaArticle)
  const isCompleted = useAppSelector(selectBootcampPageCompleted)
  const isGradePage = useIsBootcampGradePage()
  const colorInfo: CategoryColorClasses = useAppSelector(selectVideoTopicColor)
  const [didUnderstandContent, setDidUnderstandContent] = useState(false)
  const [setBootcampData] = useSetBootcampDataMutation()
  const router = useRouter()
  const { action, key: bootcampKey, level, moduleKey, subtopic } = router.query as unknown as BootcampPageUrlParamsType
  const { t } = useTranslation('bootcamp')
  const [logVideoActivity] = useAddOtherActivityAttemptMutation()
  const showFunFactChest = useShowFunFactChest()
  const isMobile = useCheckBreakpoint({ lt: Breakpoint.MD })
  const authenticated = useAppSelector(isAuthenticated)

  const dispatch = useAppDispatch()
  const [tooltipClose, setTooltipClose] = useState(false)
  const width = isMobile ? 'widest' : 'larger'
  const { isTooltipNotLoaded, hideVideoUnderstoodTooltip, setHideVideoUnderstoodTooltip } = useUserSettings()
  const [isChecked, setChecked] = useState(false)

  const handleVideoEnd = (): void => {
    dispatch(setChecklistNotification({ rewardFeature: REWARD_FEATURE.WATCH_VIDEO }))
  }

  const handleOkButtonOnClick = (): void => {
    setTooltipClose((prevState) => !prevState)
    setHideVideoUnderstoodTooltip(true)
  }

  useEffect(() => {
    setDidUnderstandContent(isCompleted)
  }, [isCompleted, level, action])

  const checkboxHandler = (val: boolean): void => {
    if (!authenticated) {
      dispatch(setShowFreemiumModalCreator(true))
      return
    }

    if (!didUnderstandContent) {
      setDidUnderstandContent(val)
      // Send selection data to firestore
      setBootcampData({
        bootcampId: bootcampKey,
        moduleId: moduleKey,
        levelId: level,
        levelPage: action,
        gymType: subtopic,
      })

      bootcampAnalytics.log(BCAEvent.CLICKED_AND_UNDERSTOOD, {
        category: subtopic,
        subject: bootcampKey,
        bootcamp: moduleKey,
        level,
        bootcamp_page_type: action,
      })
    }
  }

  // Track video play statistics
  const onVideoPlay = (): void => {
    if (authenticated) {
      logVideoActivity({
        attempts: [
          {
            status: 'play',
            activityCategory: ActivityCategory.BOOTCAMP_VIDEO,
            generationMethod: GenerationMethod.STATIC,
            path: router.asPath,
            variationKey: uuid(),
            metadata: {
              type: ContentType.bootcampVideo,
              category: subtopic,
              subject: bootcampKey,
              bootcampNumber: Number(moduleKey),
              pageLevelNumber: Number(level),
              activityType: 0,
            },
          },
        ],
      })
    }
  }
  // if encyclopediaArticle and grade page or curriculum level then hide continue button
  const isShowGrade = router.query?.showGrade === 'true'
  const hideContinueButton = (isGradePage && encyclopediaArticle) || isShowGrade
  const isCurriculumBootcamp = subtopic === CATEGORY_PATHS.curriculum
  return (
    <div data-testid="video-page-content">
      {encyclopediaArticle && (
        <TheoryArticleView
          articleBody={htmlToPlainText(encyclopediaArticle.htmlContent.html)}
          hideHeading
          isCurriculumLevel
          topic={encyclopediaArticle}
        />
      )}
      {!encyclopediaArticle && content && (
        <BlockContent
          blocks={content}
          className={styles['sanity-block']}
          onBootcampVideoEnd={handleVideoEnd}
          onBootcampVideoPlay={onVideoPlay}
        />
      )}
      <Checkbox
        centerText
        checkboxClassName={`border border-gray-placeholder h-6 w-6 ${colorInfo.borderClass} ${colorInfo.checkedBgClass}`}
        className={`${isShowGrade ? 'my-3 md:my-6.5' : 'my-3'}`}
        id="bootcamp-why"
        isChecked={didUnderstandContent}
        onChange={checkboxHandler}
        text={
          <Fragment>
            {t('done')}
            <span className="ml-2">
              <SVGIcon icon={MusicRobotIcon} size={20} />
            </span>
          </Fragment>
        }
      />
      {!(tooltipClose || hideVideoUnderstoodTooltip || isTooltipNotLoaded) && (
        <div
          className={`absolute z-20 left-1/2 transform -translate-x-1/2 -translate-y-1/2 ${
            isShowGrade ? '-mt-1 md:-mt-5' : '-mt-2'
          }`}
        >
          <TipsTooltip
            arrowPosition="bottom-center-high"
            handleOkButtonOnClick={handleOkButtonOnClick}
            hideCheckbox
            hideTooltip={tooltipClose || hideVideoUnderstoodTooltip || isTooltipNotLoaded}
            isChecked={isChecked}
            setChecked={setChecked}
            setTooltipClose={setTooltipClose}
            width={width}
          >
            <div className="flex flex-col justify-center items-center ">
              <div className="w-full mt-6 text-center leading-6">
                <span className="relative font-medium text-purple sm360:text-xs md:text-md tracking-normal leading-5 mb-1">
                  {t('tip')}{' '}
                </span>
                <span className="font-medium sm360:text-xs md:text-md tracking-normal leading-5">
                  {isCurriculumBootcamp ? t('understoodTooltip') : t('videoUnderstoodTooltip')}
                </span>
              </div>
            </div>
          </TipsTooltip>
        </div>
      )}
      {showFunFactChest && isMobile ? (
        <div className="mt-5">
          <FunFactChest />
        </div>
      ) : null}
      {!hideContinueButton && (
        <NextLevel
          borderClass={`border ${colorInfo.borderClass}`}
          className={`${colorInfo.textClass}`}
          iconColor={`${colorInfo.textClass}`}
          shouldShallowRoute={!(action === LevelActionType.Why || action === LevelActionType.Definition)}
          wrapperClass="mx-auto mt-4 md:mt-auto"
        />
      )}
      {authenticated && (
        <div className="absolute bottom-2.5 left-3 md:left-4 md:bottom-3">
          <FeedbackButton
            contentId={`${bootcampKey}#${moduleKey}#${level}#${action}`}
            contentSubtype={ContentType.bootcampVideo}
            product={FeedbackProducts.vcc.video}
          />
        </div>
      )}
    </div>
  )
}

export default VideoContentWrapper
